import { useQuery } from '@apollo/client';
import { useSelector, useDispatch } from 'react-redux';
import { useEffect, useMemo } from 'react';
import { FilterResultCounts, FilteredVehicle, RangeResultsCounts } from '../types';
import { GET_FILTERED_VEHICLES } from '../schemas/FilteredVehiclesSchema';
import { QUERY_VERSION } from '../constants';
import { selectFilters, setNonAudiCounts } from '../redux/filtersReducer';
import useFilterStringifier from './useFilterStringifier';
import { selectSortBy } from '../redux/sortByReducer';
import { selectAppConfig } from '../redux/appConfigReducer';
import { resultsReturnedEvent, resultsReturnedFailed } from '../utils/analyticsEventListeners';

interface QueryResult {
  getFilteredVehiclesForWormwood: {
    vehicles: FilteredVehicle[];
    filterResults: FilterResultCounts;
    rangeResults: RangeResultsCounts;
  };
}

const useGetVehicles = () => {
  const { confirmedFilters } = useSelector(selectFilters);
  const dispatch = useDispatch();
  const { sortBy } = useSelector(selectSortBy);
  const { marketType, isTier3page, dataSource } = useSelector(selectAppConfig);

  const { vehicleType, nonAudi } = confirmedFilters;

  const { combinedFilters, shouldListVehicles } = useFilterStringifier(confirmedFilters);

  const isUsMarket = marketType === 'USA';
  const isUsedVehicles = vehicleType === 'used';

  const market = useMemo(() => {
    if (isUsMarket) return isUsedVehicles ? 'USUC' : 'US';
    return isUsedVehicles ? 'CAUC' : 'CA';
  }, [isUsMarket, isUsedVehicles]);

  const importDb = useMemo(() => {
    if (isTier3page && dataSource === 'mock') return 'AGC_USA_NARWHAL'; // CA and US T3 and Mock Vehicles crated by NARWHAL team
    if (isTier3page && dataSource === 'jdp') {
      return isUsMarket ? 'AGC_USA_JDP' : 'AGC_CAN_JDP'; // CA OR US T3 and source JDP
    }
    if (isUsMarket) return 'TAMUSNP'; // US T1 or US T3 with default dataSource
    if (isUsedVehicles) return 'X'; // CA Used T1 or CA Used T3 with default dataSource
    return 'CANN'; // CA NEW T1 or CA NEW T3 with default dataSource
  }, [dataSource, isTier3page, isUsMarket, isUsedVehicles]);

  const nonAudiVehicles = useMemo(() => {
    if (nonAudi && isTier3page) {
      return nonAudi;
    }
    if (
      confirmedFilters.vehicleType !== 'new' &&
      confirmedFilters.usedInventoryType === 'has-warranties.combined' &&
      isTier3page
    ) {
      return '';
    }
    return 'foreign-brand.no';
  }, [confirmedFilters.usedInventoryType, confirmedFilters.vehicleType, isTier3page, nonAudi]);

  const { data, loading, fetchMore, error } = useQuery<
    QueryResult,
    {
      version?: string;
      market?: string[];
      limit?: number;
      lang?: string;
      filters?: string;
      ranges?: string;
      sort?: string;
      offset?: number;
      preset?: string;
    }
  >(GET_FILTERED_VEHICLES, {
    notifyOnNetworkStatusChange: true,
    errorPolicy: 'all',
    skip: !shouldListVehicles,
    variables: {
      version: QUERY_VERSION,
      market: [market],
      lang: 'en',
      filters: combinedFilters,
      ranges: 'prices.retail,modelYear,powerHP,usedCarMileage',
      sort: sortBy,
      // we don't need the actual vehicle list in the filter drawer,
      // so we set limit=0 in this case to speed up the process
      limit: 12,
      offset: 0,
      preset: `${nonAudiVehicles},sold-order.no,stat-import.${importDb}`,
    },
  });

  useEffect(() => {
    if (loading) return;
    const totalCount = data?.getFilteredVehiclesForWormwood?.filterResults?.totalCount;
    const nonAudiManufactures =
      data?.getFilteredVehiclesForWormwood?.filterResults?.nonAudiFilterResultCounts?.manufactures;
    const nonAudiModels =
      data?.getFilteredVehiclesForWormwood?.filterResults?.nonAudiFilterResultCounts?.models;
    dispatch(setNonAudiCounts({ nonAudiManufactures, nonAudiModels }));
    if (totalCount === 0) {
      resultsReturnedFailed();
    }
    if (totalCount && totalCount > 0) {
      resultsReturnedEvent(totalCount);
    }
  }, [data, dispatch, loading]);

  return { loading, fetchMore, error, data };
};

export default useGetVehicles;
